import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Check, X, Mic, Download, Globe, MessageSquare, Smartphone,
  Loader2, Info, Save, RefreshCw, Copy, FileDown, Upload, FolderPlus, AudioLines, Disc3, Share2, QrCode, Trophy,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { SendToPrompterButton } from "@/components/SendToPrompterButton";
import VoiceRecorderModal from "@/components/VoiceRecorderModal";
import ShoutoutMontageModal from "@/components/ShoutoutMontageModal";
import MontageSocialModal from "@/components/MontageSocialModal";
import { QRCodeCanvas } from "qrcode.react";
import { api, API_BASE } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

const BADGE = {
  pending:  "bg-brand-amber/15 text-brand-amber",
  approved: "bg-emerald-500/15 text-emerald-300",
  flagged:  "bg-red-500/15 text-red-400",
  rejected: "bg-bg-elevated text-ink-muted",
};

export default function RequestLine() {
  const [meta, setMeta] = useState(null);          // settings + voices + station
  const [form, setForm] = useState(null);          // editable settings copy
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);    // request_id being acted on
  const [generated, setGenerated] = useState(null); // last generated shoutout
  const [preview, setPreview] = useState({ name: "", song: "", artist: "", shout: "" });
  const [sendingPreview, setSendingPreview] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceForm, setVoiceForm] = useState({ name: "", file: null, consent: false });
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [previewVoice, setPreviewVoice] = useState(null);
  const [showPrevRecorder, setShowPrevRecorder] = useState(false);
  const [montages, setMontages] = useState([]);
  const [showMontage, setShowMontage] = useState(false);
  const [shareMontage, setShareMontage] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef(null);
  const pollRef = useRef(null);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/request-line/settings");
      setMeta(r.data);
      setForm(r.data.settings);
    } catch { toast.error("Failed to load Request Line settings"); }
  }, []);

  const loadQueue = useCallback(async (status) => {
    try {
      const r = await api.get(`/request-line/requests?status=${status}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch { toast.error("Failed to load requests"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  const loadMontages = useCallback(() => {
    api.get("/request-line/montage/list").then((r) => setMontages(r.data.montages || [])).catch(() => {});
  }, []);
  useEffect(() => { loadMontages(); }, [loadMontages]);

  const [board, setBoard] = useState(null);
  useEffect(() => {
    if (!meta?.station_id) return;
    api.get(`/request-line/public/${meta.station_id}/leaderboard`).then((r) => setBoard(r.data)).catch(() => {});
  }, [meta?.station_id]);

  const boardScript = board?.eligible
    ? [
        `Here's this week's most requested on ${meta?.station_name || "the station"} — counting down from number ${board.items.length}:`,
        ...[...board.items].reverse().map((it) => `Number ${it.rank} — **${it.song_title}** by ${it.artist}, with ${it.count} request${it.count === 1 ? "" : "s"}.`),
        `Want your song on next week's countdown? Send us a request — and record a shoutout in your own voice!`,
      ].join("\n\n")
    : "";
  useEffect(() => {
    setLoading(true);
    loadQueue(tab);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadQueue(tab), 30000);
    return () => clearInterval(pollRef.current);
  }, [tab, loadQueue]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/request-line/settings", form);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save settings"); }
    finally { setSaving(false); }
  };

  const act = async (id, action) => {
    setWorking(id + action);
    try {
      await api.post(`/request-line/requests/${id}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      loadQueue(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || `Failed to ${action}`); }
    finally { setWorking(null); }
  };

  const generateMp3 = async (id) => {
    setWorking(id + "mp3");
    try {
      const r = await api.post(`/request-line/requests/${id}/shoutout`);
      setGenerated({ request_id: id, ...r.data.shoutout });
      toast.success("Shoutout MP3 ready");
      loadQueue(tab);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "MP3 generation failed", { duration: 10000 });
    } finally { setWorking(null); }
  };

  const downloadMp3 = async (id, filename) => {
    try {
      const r = await api.get(`/request-line/requests/${id}/shoutout.mp3`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "shoutout.mp3"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const exportTxt = async () => {
    try {
      const r = await api.get("/request-line/export.txt", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "approved_requests.txt"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const embedSnippet = meta
    ? `<script src="${BACKEND}/api/embed/requestline.js" data-station="${meta.station_id}"></script>`
    : "";

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedSnippet);
    toast.success("Embed code copied");
  };

  const sendPreview = async () => {
    if (!preview.song.trim() || !preview.artist.trim()) {
      toast.error("Song title and artist are required"); return;
    }
    setSendingPreview(true);
    try {
      let voiceId = "";
      if (previewVoice) {
        const fd = new FormData();
        fd.append("audio", previewVoice, previewVoice.name || "shoutout.wav");
        const vr = await api.post(`/request-line/public/${meta.station_id}/voice-shoutout`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        voiceId = vr.data.voice_upload_id;
      }
      await api.post(`/request-line/public/${meta.station_id}/submit`, {
        listener_name: preview.name, song_title: preview.song,
        artist: preview.artist, shoutout_text: preview.shout,
        voice_upload_id: voiceId,
      });
      toast.success("Test request submitted — check the queue");
      setPreview({ name: "", song: "", artist: "", shout: "" });
      setPreviewVoice(null);
      loadQueue(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setSendingPreview(false); }
  };

  const voiceToLibrary = async (id) => {
    setWorking(id + "lib");
    try {
      const r = await api.post(`/request-line/requests/${id}/voice-to-library`);
      toast.success(r.data.already_added
        ? "Already in your Imaging library"
        : "Added to your Imaging library — play it from the Sequencer or drop it into playout");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to library"); }
    finally { setWorking(null); }
  };

  const uploadVoice = async () => {
    if (!voiceForm.name.trim()) { toast.error("Give the voice a name"); return; }
    if (!voiceForm.file) { toast.error("Choose an audio file (≥30s of clean speech)"); return; }
    if (!voiceForm.consent) { toast.error("Consent confirmation is required"); return; }
    setUploadingVoice(true);
    try {
      const fd = new FormData();
      fd.append("name", voiceForm.name.trim());
      fd.append("description", "Request Line shoutout voice");
      fd.append("consent_attestation", "true");
      fd.append("sample", voiceForm.file);
      const r = await api.post("/artist-drops/voices/clone", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const newId = r.data.voice_id;
      const updated = { ...form, shoutout_voice_id: newId };
      setForm(updated);
      await api.put("/request-line/settings", updated);
      await loadSettings();
      setForm((f) => ({ ...f, shoutout_voice_id: newId }));
      setShowVoiceModal(false);
      setVoiceForm({ name: "", file: null, consent: false });
      toast.success(`Voice "${r.data.name}" uploaded and set as your shoutout voice`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Voice upload failed", { duration: 10000 });
    } finally { setUploadingVoice(false); }
  };

  const TABS = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "flagged", label: "Flagged" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="max-w-6xl" data-testid="request-line-page">
      <PageHeader
        eyebrow="Request Line"
        title="Listener song requests."
        description="A public widget your listeners use to request songs and shoutouts. Everything lands here for review — nothing touches your playout system."
      />

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 mb-6">
        {/* ── Public widget preview ─────────────────────────── */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">// Public widget — live preview</div>
          <div
            data-testid="widget-preview"
            className="rounded-2xl border border-line p-6 relative overflow-hidden"
            style={{ background: "linear-gradient(180deg,#151a27,#0d0f16)" }}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
                 style={{ background: "radial-gradient(circle,#4a9eff22,transparent 70%)" }} />
            <div className="font-display font-bold text-[15px]">🎙 {meta?.station_name || "…"}</div>
            <div className="text-[11px] text-ink-muted mb-4">Request a song — we might play it live</div>

            <label className="block text-[11px] text-ink-secondary mb-1.5">Your name (or nickname)</label>
            <input data-testid="preview-name" value={preview.name} maxLength={60}
              onChange={(e) => setPreview({ ...preview, name: e.target.value })}
              placeholder="e.g. Maria from Warwick"
              className="w-full bg-[#0d0f16] border border-line rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:border-brand-blue" />

            <div className="flex gap-2 mt-3">
              <div className="flex-1">
                <label className="block text-[11px] text-ink-secondary mb-1.5">Song title</label>
                <input data-testid="preview-song" value={preview.song} maxLength={120}
                  onChange={(e) => setPreview({ ...preview, song: e.target.value })}
                  placeholder="Song title"
                  className="w-full bg-[#0d0f16] border border-line rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:border-brand-blue" />
              </div>
              <div className="flex-1">
                <label className="block text-[11px] text-ink-secondary mb-1.5">Artist</label>
                <input data-testid="preview-artist" value={preview.artist} maxLength={120}
                  onChange={(e) => setPreview({ ...preview, artist: e.target.value })}
                  placeholder="Artist"
                  className="w-full bg-[#0d0f16] border border-line rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:border-brand-blue" />
              </div>
            </div>

            <label className="block text-[11px] text-ink-secondary mb-1.5 mt-3">Shoutout message (optional)</label>
            <textarea data-testid="preview-shoutout" value={preview.shout} maxLength={140}
              onChange={(e) => setPreview({ ...preview, shout: e.target.value })}
              placeholder="e.g. Happy birthday to my sister Kayla!"
              className="w-full bg-[#0d0f16] border border-line rounded-lg px-3 py-2.5 text-[13px] min-h-[60px] resize-y focus:outline-none focus:border-brand-blue" />
            <div className="text-[10px] text-ink-muted text-right mt-1">{preview.shout.length} / 140 characters</div>

            <label className="block text-[11px] text-ink-secondary mb-1.5 mt-3">Or say it in their own voice (optional, 30s max)</label>
            {previewVoice ? (
              <div className="flex items-center gap-2 rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-2" data-testid="preview-voice-chip">
                <AudioLines className="h-3.5 w-3.5 text-purple-300 shrink-0" />
                <span className="flex-1 min-w-0 text-[11.5px] text-purple-200 truncate">
                  {previewVoice.name || "Recorded shoutout"} · {(previewVoice.size / 1024).toFixed(0)} KB
                </span>
                <button data-testid="preview-voice-remove" onClick={() => setPreviewVoice(null)}
                  className="text-ink-muted hover:text-red-400 transition"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button data-testid="preview-voice-record" onClick={() => setShowPrevRecorder(true)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-line py-2.5 text-[11.5px] text-ink-secondary hover:border-brand-blue hover:text-brand-blue transition">
                  <Mic className="h-3 w-3" /> Record
                </button>
                <label data-testid="preview-voice-upload"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-line py-2.5 text-[11.5px] text-ink-secondary hover:border-brand-blue hover:text-brand-blue transition cursor-pointer">
                  <Upload className="h-3 w-3" /> Upload audio
                  <input type="file" accept="audio/*,.mp3,.m4a" className="hidden" data-testid="preview-voice-file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        if (f.size > 15 * 1024 * 1024) toast.error("Keep the shoutout under about 30 seconds");
                        else setPreviewVoice(f);
                      }
                      e.target.value = "";
                    }} />
                </label>
              </div>
            )}

            <button data-testid="preview-submit-btn" onClick={sendPreview} disabled={sendingPreview}
              className="w-full mt-4 rounded-lg py-3 text-[13px] font-bold text-[#08101f] disabled:opacity-60"
              style={{ background: "#4a9eff" }}>
              {sendingPreview ? "Sending…" : "📻 Send request"}
            </button>
            <div className="text-[10px] text-ink-muted text-center mt-3 leading-relaxed">
              Requests are reviewed before airing. We can't guarantee a specific play time.
            </div>
          </div>

          {/* Embed snippet */}
          <div className="mt-4 rounded-xl border border-line bg-bg-surface p-4" data-testid="embed-snippet-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// Embed on your website</div>
            <code className="block text-[10.5px] font-mono text-ink-secondary bg-bg-elevated rounded-lg p-3 break-all">{embedSnippet}</code>
            <button data-testid="copy-embed-btn" onClick={copyEmbed}
              className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-line hover:border-brand-blue hover:text-brand-blue transition">
              <Copy className="h-3 w-3" /> Copy embed code
            </button>
            {meta?.station_id && (
              <div className="mt-3 pt-3 border-t border-line" data-testid="public-page-row">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">// Or share your hosted request page</div>
                <div className="flex items-center gap-2">
                  <a href={`/requests/${meta.station_id}`} target="_blank" rel="noreferrer" data-testid="public-page-link"
                    className="flex-1 min-w-0 truncate text-[10.5px] font-mono text-brand-blue hover:underline">
                    {`${window.location.origin}/requests/${meta.station_id}`}
                  </a>
                  <button data-testid="show-qr-btn" onClick={() => setShowQr(true)}
                    className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded-md border border-line hover:border-brand-blue hover:text-brand-blue transition shrink-0">
                    <QrCode className="h-3 w-3" /> QR
                  </button>
                  <button data-testid="copy-public-page-btn"
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/requests/${meta.station_id}`); toast.success("Public request page link copied"); }}
                    className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded-md border border-line hover:border-brand-blue hover:text-brand-blue transition shrink-0">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: channels + settings ─────────────── */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">// Intake channels</div>
          <div className="grid sm:grid-cols-3 gap-2.5 mb-5">
            <div className="rounded-xl border border-line bg-bg-surface p-3.5 flex items-center gap-2.5" data-testid="channel-web">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-brand-blue/10 text-brand-blue shrink-0"><Globe className="h-4 w-4" /></span>
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold">Web widget</div>
                <div className="text-[10.5px] text-ink-muted">Embeddable on station site</div>
              </div>
            </div>
            <div className="rounded-xl border border-line bg-bg-surface p-3.5 flex items-center gap-2.5 opacity-55" data-testid="channel-sms">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-brand-amber/10 text-brand-amber shrink-0"><MessageSquare className="h-4 w-4" /></span>
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold">SMS / Text-in</div>
                <div className="text-[10.5px] text-ink-muted">Phase 2 — needs carrier setup</div>
              </div>
            </div>
            <div className="rounded-xl border border-line bg-bg-surface p-3.5 flex items-center gap-2.5 opacity-55" data-testid="channel-social">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-brand-amber/10 text-brand-amber shrink-0"><Smartphone className="h-4 w-4" /></span>
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold">Social DM intake</div>
                <div className="text-[10.5px] text-ink-muted">Phase 2 — via Social Connections</div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="settings-card">
            <div className="flex items-center justify-between mb-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// Rate limits &amp; moderation settings</div>
              <button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving || !form}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-brand-amber text-bg font-bold hover:bg-brand-amber/90 transition disabled:opacity-50">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
              </button>
            </div>
            {form && (
              <div>
                <div className="flex items-center justify-between py-2.5 border-b border-line text-[12.5px]">
                  <span>Requests per listener</span>
                  <span className="flex items-center gap-2">
                    <input data-testid="rate-limit-input" type="number" min={1} max={50} value={form.rate_limit_per_hour}
                      onChange={(e) => setForm({ ...form, rate_limit_per_hour: parseInt(e.target.value || "3", 10) })}
                      className="w-16 bg-bg-elevated border border-line rounded-md px-2 py-1 text-right text-[12.5px]" />
                    <span className="text-ink-muted">per hour</span>
                  </span>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-line text-[12.5px]">
                  <span>Auto-filter profanity</span>
                  <Switch data-testid="profanity-toggle" checked={form.profanity_filter_enabled}
                    onCheckedChange={(v) => setForm({ ...form, profanity_filter_enabled: v })} />
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-line text-[12.5px]">
                  <span>Auto-filter spam / links</span>
                  <Switch data-testid="spam-toggle" checked={form.spam_filter_enabled}
                    onCheckedChange={(v) => setForm({ ...form, spam_filter_enabled: v })} />
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-line text-[12.5px]">
                  <span>Auto-approve known clean requests</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[10.5px] text-ink-muted">{form.auto_approve_enabled ? "On" : "Off — manual review"}</span>
                    <Switch data-testid="auto-approve-toggle" checked={form.auto_approve_enabled}
                      onCheckedChange={(v) => setForm({ ...form, auto_approve_enabled: v })} />
                  </span>
                </div>
                <div className="flex items-center justify-between py-2.5 text-[12.5px]">
                  <span>Shoutout voice (MP3 generation)</span>
                  <span className="flex items-center gap-2">
                    <select data-testid="voice-select" value={form.shoutout_voice_id}
                      onChange={(e) => setForm({ ...form, shoutout_voice_id: e.target.value })}
                      className="bg-bg-elevated border border-line rounded-md px-2 py-1.5 text-[12.5px] max-w-[180px]">
                      {(meta?.voices || []).map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                    <button data-testid="upload-voice-btn" onClick={() => setShowVoiceModal(true)}
                      className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-line hover:border-brand-blue hover:text-brand-blue transition whitespace-nowrap">
                      <Mic className="h-3 w-3" /> Upload voice
                    </button>
                  </span>
                </div>
                <div className="mt-3 rounded-lg border border-line bg-bg-elevated p-3 text-[11.5px] text-ink-secondary leading-relaxed flex gap-2.5">
                  <Info className="h-4 w-4 text-brand-amber shrink-0 mt-0.5" />
                  <span>Requests never queue directly into your playout system. They land here for a human to review, then get manually cued in Myriad (or your automation software) — BroadcastFlow AI doesn't control on-air playback.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Moderation queue ──────────────────────────────── */}
      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="moderation-queue">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">// Internal — Moderation queue</div>
            <div className="flex gap-1.5">
              {TABS.map((t) => (
                <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`text-xs px-3 py-1.5 rounded-md border transition ${
                    tab === t.key
                      ? "bg-brand-blue/10 text-brand-blue border-brand-blue/40"
                      : "text-ink-secondary border-line hover:border-brand-blue/40"}`}>
                  {t.label}{counts[t.key] != null ? ` (${counts[t.key]})` : ""}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="refresh-queue-btn" onClick={() => loadQueue(tab)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line hover:border-brand-blue hover:text-brand-blue transition">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
            <button data-testid="export-txt-btn" onClick={exportTxt}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line bg-bg-elevated font-semibold hover:border-brand-blue hover:text-brand-blue transition">
              <FileDown className="h-3.5 w-3.5" /> Export approved (.txt)
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-14 font-mono text-xs text-ink-muted">LOADING…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 text-sm text-ink-muted" data-testid="queue-empty">
            No {tab === "all" ? "" : tab + " "}requests yet. Share the widget with your listeners!
          </div>
        ) : (
          <div>
            {items.map((r) => {
              const flagged = r.status === "flagged";
              const canMp3 = r.status === "pending" || r.status === "approved";
              return (
                <div key={r.request_id} data-testid={`request-row-${r.request_id}`}
                  className="flex items-center gap-3 py-3 border-b border-line last:border-b-0">
                  <span className="w-9 h-9 rounded-full shrink-0"
                    style={{ background: "linear-gradient(135deg,#4a9eff,#8a5fff)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate">"{r.song_title}" — {r.artist}</div>
                    <div className="text-[11px] text-ink-muted">
                      Requested by {r.listener_name || "Anonymous"} · {timeAgo(r.submitted_at)} · Web widget
                    </div>
                    {r.shoutout_text && (
                      <div className="text-[11.5px] text-ink-secondary italic mt-0.5 truncate">"{r.shoutout_text}"</div>
                    )}
                    {r.voice_shoutout && (
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`voice-shoutout-${r.request_id}`}>
                        <span className="inline-flex items-center gap-1 rounded bg-purple-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-purple-300 shrink-0">
                          <AudioLines className="h-2.5 w-2.5" /> Listener voice
                        </span>
                        <audio controls preload="none" className="h-8 max-w-[230px]"
                          src={`${API_BASE}/request-line/requests/${r.request_id}/voice-shoutout.audio`}
                          data-testid={`voice-audio-${r.request_id}`} />
                        <span className="text-[11px] text-ink-secondary italic min-w-0 truncate max-w-[300px]"
                          data-testid={`voice-transcript-${r.request_id}`}>
                          {r.voice_shoutout.transcript
                            ? `"${r.voice_shoutout.transcript}"`
                            : r.voice_shoutout.transcribe_status === "done"
                            ? "No speech detected — listen before approving"
                            : r.voice_shoutout.transcribe_status === "failed"
                            ? "Transcript unavailable — listen before approving"
                            : "Transcribing…"}
                        </span>
                        {r.status === "approved" && (
                          <button data-testid={`voice-to-library-${r.request_id}`}
                            onClick={() => voiceToLibrary(r.request_id)} disabled={working === r.request_id + "lib"}
                            className="h-7 px-2 rounded-md flex items-center gap-1 bg-purple-500/10 border border-purple-500/30 text-purple-300 text-[10.5px] font-semibold hover:bg-purple-500/20 transition disabled:opacity-50">
                            {working === r.request_id + "lib" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FolderPlus className="h-3 w-3" />} To Library
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`text-[9px] font-bold px-2 py-1 rounded uppercase tracking-wide shrink-0 ${BADGE[r.status] || BADGE.pending}`}>
                    {flagged ? (r.flag_reason || "Flagged") : r.status}
                  </span>
                  <div className="flex gap-1.5 shrink-0">
                    {r.status !== "approved" && (
                      <button data-testid={`approve-btn-${r.request_id}`} title="Approve"
                        onClick={() => act(r.request_id, "approve")} disabled={working === r.request_id + "approve"}
                        className="w-8 h-8 rounded-md flex items-center justify-center bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50">
                        {working === r.request_id + "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button data-testid={`reject-btn-${r.request_id}`} title="Reject"
                        onClick={() => act(r.request_id, "reject")} disabled={working === r.request_id + "reject"}
                        className="w-8 h-8 rounded-md flex items-center justify-center bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition disabled:opacity-50">
                        {working === r.request_id + "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    {canMp3 && !flagged && (
                      r.shoutout ? (
                        <button data-testid={`download-mp3-btn-${r.request_id}`} title="Download shoutout MP3"
                          onClick={() => downloadMp3(r.request_id, r.shoutout.filename)}
                          className="h-8 px-2.5 rounded-md flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-semibold hover:bg-emerald-500/20 transition">
                          <Download className="h-3 w-3" /> MP3
                        </button>
                      ) : (
                        <button data-testid={`generate-mp3-btn-${r.request_id}`} title="Generate MP3 shoutout"
                          onClick={() => generateMp3(r.request_id)} disabled={working === r.request_id + "mp3"}
                          className="h-8 px-2.5 rounded-md flex items-center gap-1 bg-brand-blue/10 border border-brand-blue/30 text-brand-blue text-[11px] font-semibold hover:bg-brand-blue/20 transition disabled:opacity-50">
                          {working === r.request_id + "mp3" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mic className="h-3 w-3" />} MP3
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Most requested this week ──────────────────────── */}
      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="leaderboard-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Trophy className="h-3 w-3 text-brand-amber" /> // Most requested this week
            </div>
            <div className="text-[12px] text-ink-secondary">
              {board?.eligible
                ? "Live top-5 from your listeners — also shown on your public request page."
                : `Appears here and on your public request page once 3+ different songs are requested in a week${board ? ` (currently ${board.distinct_count})` : ""}.`}
            </div>
          </div>
          {board?.eligible && (
            <SendToPrompterButton
              title="Most requested this week"
              text={boardScript}
              source="request_line_leaderboard"
              testid="leaderboard-to-prompter"
            />
          )}
        </div>
        {board?.eligible && (
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="leaderboard-dash-items">
            {board.items.map((it) => (
              <div key={it.rank} data-testid={`leaderboard-dash-row-${it.rank}`}
                className={`rounded-lg border px-3 py-2.5 ${it.rank === 1 ? "border-brand-amber/50 bg-brand-amber/5" : "border-line bg-bg-elevated"}`}>
                <div className={`font-display font-bold ${it.rank === 1 ? "text-brand-amber" : "text-ink-muted"}`}>#{it.rank}</div>
                <div className="text-[12.5px] font-semibold truncate">{it.song_title}</div>
                <div className="text-[10.5px] text-ink-muted truncate">{it.artist} · {it.count} request{it.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Shoutout Roundup — weekly montage ─────────────── */}
      <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="montage-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">// Shoutout Roundup — weekly montage</div>
            <div className="text-[12px] text-ink-secondary">Stitch approved listener voice shoutouts into one ready-to-air segment — music bed included, zero editing.</div>
          </div>
          <button data-testid="montage-open-btn" onClick={() => setShowMontage(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-purple-500/10 border border-purple-500/40 px-4 py-2 text-sm font-semibold text-purple-300 hover:bg-purple-500/20 transition">
            <Disc3 className="h-4 w-4" /> Build montage
          </button>
        </div>
        {montages.length > 0 && (
          <div className="mt-3 space-y-2">
            {montages.slice(0, 3).map((m) => (
              <div key={m.montage_id} data-testid={`montage-row-${m.montage_id}`}
                className="flex items-center gap-3 rounded-lg border border-line bg-bg-elevated px-3 py-2">
                <Disc3 className="h-4 w-4 text-purple-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold truncate">{m.title}</div>
                  <div className="text-[10.5px] text-ink-muted truncate">
                    {m.clip_count} shoutout{m.clip_count === 1 ? "" : "s"} · {(m.duration_ms / 1000).toFixed(0)}s
                    {m.bed_label ? ` · bed: ${m.bed_label}` : ""} · {timeAgo(m.created_at)} · in Imaging library
                  </div>
                </div>
                <audio controls preload="none" className="h-8 max-w-[230px]" src={`${BACKEND}${m.audio_url}`}
                  data-testid={`montage-audio-${m.montage_id}`} />
                <button data-testid={`montage-share-${m.montage_id}`} onClick={() => setShareMontage(m)}
                  title="Post this roundup to your social accounts"
                  className="h-8 px-2.5 rounded-md flex items-center gap-1 bg-brand-amber/10 border border-brand-amber/40 text-brand-amber text-[10.5px] font-semibold hover:bg-brand-amber/20 transition shrink-0">
                  <Share2 className="h-3 w-3" /> Post to socials
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Generated shoutout panel ──────────────────────── */}
      {generated && (
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="generated-shoutout-panel">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-4">// Generated shoutout — ready to drop into your station library</div>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full shrink-0"
              style={{ background: "linear-gradient(135deg,#3ddc97,#1a8a5c)" }} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{generated.filename}</div>
              <div className="text-[11px] text-ink-muted">
                Voice: {generated.voice_label} · {(generated.size_bytes / 1024).toFixed(0)} KB · MP3 · Generated {timeAgo(generated.generated_at)}
              </div>
              <div className="text-[11.5px] text-ink-secondary italic mt-0.5">"{generated.script}"</div>
            </div>
            <span className={`text-[9px] font-bold px-2 py-1 rounded uppercase tracking-wide shrink-0 ${BADGE.approved}`}>Ready</span>
            <SendToPrompterButton
              title={`Shoutout — ${generated.filename || "request"}`}
              text={generated.script}
              source="request_line"
              testid="rl-send-prompter"
            />
            <button data-testid="download-generated-btn"
              onClick={() => downloadMp3(generated.request_id, generated.filename)}
              className="h-8 px-3.5 rounded-md flex items-center gap-1.5 font-bold text-[11.5px] text-[#08101f] shrink-0"
              style={{ background: "#4a9eff" }}>
              <Download className="h-3.5 w-3.5" /> Download MP3
            </button>
          </div>
          <div className="mt-3.5 rounded-lg border border-line bg-bg-elevated p-3 text-[11.5px] text-ink-secondary leading-relaxed flex gap-2.5">
            <Info className="h-4 w-4 text-brand-amber shrink-0 mt-0.5" />
            <span>Download the file and drop it directly into your playout software's media library (e.g. Myriad) — no re-recording needed. Uses the same voice personas as Voice Drops, with the same real-artist-likeness guardrails: the listener's name and shoutout text are spoken freely, but the voice itself is always a station persona or your own consenting talent, never a real musician.</span>
          </div>
        </div>
      )}
      {/* ── Upload voice modal ────────────────────────────── */}
      {showVoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="voice-modal">
          <div className="w-full max-w-md rounded-xl border border-line bg-bg-surface p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display font-bold text-lg">Upload your voice</div>
              <button data-testid="voice-modal-close" onClick={() => setShowVoiceModal(false)}
                className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="text-xs text-ink-secondary mb-4 leading-relaxed">
              Upload ≥30 seconds of clean speech (MP3, WAV, M4A or OGG). We'll create a custom
              voice you can use for shoutout MP3s — same pipeline as Voice Drops.
            </div>

            <label className="block text-[11px] text-ink-secondary mb-1.5">Voice name</label>
            <input data-testid="voice-name-input" value={voiceForm.name} maxLength={80}
              onChange={(e) => setVoiceForm({ ...voiceForm, name: e.target.value })}
              placeholder="e.g. Hans — Station Voice"
              className="w-full bg-bg-elevated border border-line rounded-lg px-3 py-2.5 text-[13px] mb-3 focus:outline-none focus:border-brand-blue" />

            <label className="block text-[11px] text-ink-secondary mb-1.5">Audio sample</label>
            <input data-testid="voice-file-input" type="file" accept="audio/*,.m4a"
              onChange={(e) => setVoiceForm({ ...voiceForm, file: e.target.files?.[0] || null })}
              className="w-full text-[12px] text-ink-secondary file:mr-3 file:rounded-md file:border-0 file:bg-brand-blue/15 file:text-brand-blue file:px-3 file:py-2 file:text-xs file:font-semibold file:cursor-pointer mb-3" />
            {voiceForm.file && (
              <div className="text-[11px] text-ink-muted mb-3 font-mono truncate">
                {voiceForm.file.name} · {(voiceForm.file.size / 1024 / 1024).toFixed(1)} MB
              </div>
            )}

            <label className="flex items-start gap-2.5 text-[11.5px] text-ink-secondary leading-relaxed cursor-pointer mb-4">
              <input data-testid="voice-consent-checkbox" type="checkbox" checked={voiceForm.consent}
                onChange={(e) => setVoiceForm({ ...voiceForm, consent: e.target.checked })}
                className="mt-0.5 accent-[#4a9eff]" />
              <span>I confirm this is <strong>my own voice</strong>, or I have explicit written consent
              from the person whose voice this is. Never a real musician or public figure.</span>
            </label>

            <button data-testid="voice-upload-submit" onClick={uploadVoice} disabled={uploadingVoice}
              className="w-full rounded-lg py-3 text-[13px] font-bold text-[#08101f] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: "#4a9eff" }}>
              {uploadingVoice ? (<><Loader2 className="h-4 w-4 animate-spin" /> Uploading &amp; cloning…</>) : (<><Mic className="h-4 w-4" /> Upload voice</>)}
            </button>
          </div>
        </div>
      )}
      {showPrevRecorder && (
        <VoiceRecorderModal
          title="Record a voice shoutout (30s max)"
          onCancel={() => setShowPrevRecorder(false)}
          onUse={(file) => { setShowPrevRecorder(false); setPreviewVoice(file); }}
        />
      )}
      {showMontage && (
        <ShoutoutMontageModal
          onClose={() => setShowMontage(false)}
          onRendered={loadMontages}
          onShare={(m) => { setShowMontage(false); setShareMontage(m); }}
        />
      )}
      {shareMontage && (
        <MontageSocialModal
          montage={shareMontage}
          stationName={meta?.station_name}
          stationId={meta?.station_id}
          onClose={() => setShareMontage(null)}
        />
      )}
      {showQr && meta?.station_id && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="request-qr-modal">
          <div className="w-full max-w-sm rounded-xl border border-line bg-bg-surface p-6 text-center">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display font-bold text-lg">Request Line QR</div>
              <button onClick={() => setShowQr(false)} data-testid="request-qr-close"
                className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="text-[11px] text-ink-secondary text-left mb-3">
              Flash it on stream overlays, posters or the studio cam — listeners scan to request a song and record a voice shoutout.
            </div>
            <div className="mx-auto w-fit rounded-xl bg-white p-4" data-testid="request-qr-code">
              <QRCodeCanvas ref={qrRef} value={`${window.location.origin}/requests/${meta.station_id}`} size={220} includeMargin={false} />
            </div>
            <div className="font-mono text-[10.5px] text-brand-blue break-all my-3">
              {`${window.location.origin}/requests/${meta.station_id}`}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="request-qr-download"
                onClick={() => {
                  const canvas = qrRef.current;
                  if (!canvas) return;
                  const a = document.createElement("a");
                  a.href = canvas.toDataURL("image/png");
                  a.download = "request-line-qr.png";
                  a.click();
                }}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-blue py-2 text-sm font-semibold text-[#08101f]">
                <Download className="h-3.5 w-3.5" /> Download PNG
              </button>
              <button data-testid="request-qr-copy"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/requests/${meta.station_id}`); toast.success("Link copied"); }}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-line bg-bg-elevated py-2 text-sm font-semibold text-ink-secondary hover:text-brand-blue transition">
                <Copy className="h-3.5 w-3.5" /> Copy link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
